import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Logo } from './Logo';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    if (sectionId === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    // If we're on the home page, just scroll
    if (location.pathname === '/') {
      e.preventDefault();
      scrollToSection(sectionId);
    }
    // Otherwise, let the link navigate to home with hash
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <Logo size={36} />
            <span className="font-bold text-xl text-gray-900">BeyondPages</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-8">
            <a 
              href="/" 
              onClick={(e) => handleNavClick(e, 'top')}
              className="text-gray-700 hover:text-blue-600 transition duration-200"
            >
              Home
            </a>
            <a 
              href="/#features" 
              onClick={(e) => handleNavClick(e, 'features')}
              className="text-gray-700 hover:text-blue-600 transition duration-200"
            >
              Leistungen
            </a>
            <a 
              href="/#pricing" 
              onClick={(e) => handleNavClick(e, 'pricing')}
              className="text-gray-700 hover:text-blue-600 transition duration-200"
            >
              Preise
            </a>
            <a 
              href="/#video" 
              onClick={(e) => handleNavClick(e, 'video')}
              className="text-gray-700 hover:text-blue-600 transition duration-200"
            >
              Demos
            </a>
            <a 
              href="/#faq" 
              onClick={(e) => handleNavClick(e, 'faq')}
              className="text-gray-700 hover:text-blue-600 transition duration-200"
            >
              FAQ
            </a>
            <a 
              href="/#contact" 
              onClick={(e) => handleNavClick(e, 'contact')}
              className="text-gray-700 hover:text-blue-600 transition duration-200"
            >
              Kontakt
            </a>
            <Link to="/qr/login" className="text-gray-700 hover:text-blue-600 transition duration-200">
              QR Management
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="text-gray-700 hover:text-blue-600 focus:outline-none"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <a
              href="/"
              onClick={(e) => handleNavClick(e, 'top')}
              className="block px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md"
            >
              Home
            </a>
            <a
              href="/#features"
              onClick={(e) => handleNavClick(e, 'features')}
              className="block px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md"
            >
              Leistungen
            </a>
            <a
              href="/#pricing"
              onClick={(e) => handleNavClick(e, 'pricing')}
              className="block px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md"
            >
              Preise
            </a>
            <a
              href="/#video"
              onClick={(e) => handleNavClick(e, 'video')}
              className="block px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md"
            >
              Demos
            </a>
            <a
              href="/#faq"
              onClick={(e) => handleNavClick(e, 'faq')}
              className="block px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md"
            >
              FAQ
            </a>
            <a
              href="/#contact"
              onClick={(e) => handleNavClick(e, 'contact')}
              className="block px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md"
            >
              Kontakt
            </a>
            <Link
              to="/qr/login"
              className="block px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md"
              onClick={() => setIsMenuOpen(false)}
            >
              QR Management
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;